import type { MouseEvent } from "react";

import { isHttps } from "@/utils/core/urlHelper";

/**
 * Prevents the browser menu from opening on protected image previews.
 *
 * @param {MouseEvent<HTMLElement>} event - Image preview context menu event.
 * @returns {void}
 */
export const preventImagePreviewContextMenu = (
  event: MouseEvent<HTMLElement>,
): void => {
  event.preventDefault();
};

/**
 * Validates whether a URL resolves to a loadable image within a timeout.
 *
 * @param {string} imageUrl - The image URL to validate.
 * @returns {Promise<boolean>} Whether the URL loads as an image within the timeout.
 */
export const isValidImgLink = (imageUrl: string): Promise<boolean> =>
  new Promise((resolve) => {
    const image = new window.Image();
    const finishValidation = (isValidImage: boolean): void => {
      window.clearTimeout(timeoutId);
      image.onload = null;
      image.onerror = null;
      resolve(isValidImage);
    };
    const timeoutId = window.setTimeout(() => finishValidation(false), 5000);

    image.onload = (): void => finishValidation(true);
    image.onerror = (): void => finishValidation(false);
    image.src = imageUrl;
  });

export const createImage = (url: string): Promise<unknown> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

/**
 * Returns the angle between two points.
 * @param {number} degreeValue - The degree value
 * @returns {number} - The radian angle
 */
export function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

/**
 * @description Returns the size of the image after rotation.
 * @param {number} width - The width of the image
 * @param {number} height - The height of the image
 * @param {number} rotation - The rotation angle
 * @returns {object} - The size of the image after rotation
 */
export function rotateSize(
  width: number,
  height: number,
  rotation: number,
): {
  width: number;
  height: number;
} {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

type PixelCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * @description This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {string} imageSrc - The image source
 * @param {PixelCrop} pixelCrop - The pixel crop
 * @param {number} rotation - The rotation angle
 * @param {object} flip - The flip object
 * @returns {Promise<string>} - The cropped image as a blob
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: PixelCrop,
  rotation: number = 0,
  flip = { horizontal: false, vertical: false },
): Promise<string | null> {
  const image: HTMLImageElement = (await createImage(
    imageSrc,
  )) as HTMLImageElement;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");

  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  // As Base64 string
  // return croppedCanvas.toDataURL('image/jpeg');

  // As a blob
  return new Promise((resolve, _reject) => {
    croppedCanvas.toBlob((file) => {
      if (file) {
        resolve(URL.createObjectURL(file));
      }
    }, "image/png");
  });
}

export const isValidBlobUrl = (url: string): boolean => {
  return /^blob:https?:\/\/[^\s]+$/i.test(url);
};

const isValidBase64 = (str: string): boolean => {
  const base64Regex =
    /^data:image\/[a-zA-Z0-9.+-]+;base64,[a-zA-Z0-9+/]+={0,2}$/;

  if (!base64Regex.test(str)) {
    return false;
  }

  try {
    const [, base64Part] = str.split(",");
    if (base64Part.length % 4 !== 0) {
      return false;
    }
    atob(base64Part);

    return true;
  } catch (e) {
    return false;
  }
};

export const isValidImageSrc = (src?: string): boolean => {
  if (!src || typeof src !== "string") {
    return false;
  }

  switch (true) {
    case src.startsWith("http"):
      return isHttps(src);
    case src.startsWith("blob:"):
      return isValidBlobUrl(src);
    case src.startsWith("data:image/"):
      return isValidBase64(src);
    case src.startsWith("/") || src.startsWith("./") || src.startsWith("../"):
      return true;
    default:
      return false;
  }
};

export const isBase64 = (str: string): boolean => {
  const base64Data = str.startsWith("data:") ? str.split("base64,")[1] : str;

  if (!base64Data) {
    return false;
  }

  try {
    atob(base64Data);

    return true;
  } catch {
    return false;
  }
};

export const proxyWithServer = (url: string, type?: "base64"): string => {
  if (!url) {
    return "";
  }
  if (!isHttps(url)) {
    return url;
  }

  // Avoid wrapping a URL that already points to the backend s3-proxy endpoint.
  try {
    const parsedUrl = new URL(url);
    if (
      parsedUrl.pathname.endsWith("/general/s3-proxy") &&
      parsedUrl.searchParams.has("url")
    ) {
      return url;
    }
  } catch {
    // Fall through and build a proxied URL when parsing fails.
  }

  const encoded = encodeURIComponent(url);
  let query = `url=${encoded}`;
  if (type) {
    query += `&type=${encodeURIComponent(type)}`;
  }

  return `${import.meta.env.VITE_BACKENDURL}/general/s3-proxy?${query}`;
};

export const convertSvgToPngFile = (
  svg: SVGElement,
  name: string,
): Promise<File> =>
  new Promise((resolve, reject) => {
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const image = new Image();

    image.onload = (): void => {
      canvas.width = image.width;
      canvas.height = image.height;
      context?.drawImage(image, 0, 0);
      canvas.toBlob((blob): void => {
        if (!blob) {
          reject(new Error("Failed to create blob"));

          return;
        }

        resolve(new File([blob], name, { type: "image/png" }));
      }, "image/png");
    };

    image.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
  });

/**
 * Converts an external image URL to an inline base64 PNG data URL by drawing
 * it onto a canvas. This prevents Kendo's drawDOM from re-fetching and
 * re-rendering the image — which has a known bug that rotates landscape images
 * 90° during PDF export.
 *
 * @param {string} url - The image URL to inline.
 * @returns {Promise<string>} A base64 PNG data URL.
 */
export const toInlineDataUrl = (url: string): Promise<string> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = (): void => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d")?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    // Fall back to original URL if canvas conversion fails
    img.onerror = (): void => resolve(url);
    img.src = url;
  });

/**
 * Walks all `img.export-image` elements inside a container and replaces their
 * `src` with inline base64 data URLs before Kendo's drawDOM runs.
 * This is the workaround for the Kendo Drawing bug where external-URL landscape
 * images are rendered rotated 90° in the exported PDF.
 *
 * @param {HTMLElement} container - The root element Kendo will capture.
 * @returns {Promise<void>}
 */
export const inlineExportImages = async (
  container: HTMLElement,
): Promise<void> => {
  const exportImages = Array.from(
    container.querySelectorAll<HTMLImageElement>("img.export-image"),
  );

  await Promise.all(
    exportImages.map(async (img) => {
      const inlineUrl = await toInlineDataUrl(img.src);
      img.src = inlineUrl;
    }),
  );
};

export const convertSvgToPngDataUrl = (svg: SVGElement): Promise<string> =>
  new Promise((resolve, reject) => {
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const image = new Image();

    image.onload = (): void => {
      canvas.width = image.width;
      canvas.height = image.height;
      context?.drawImage(image, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };

    image.onerror = (): void => {
      reject(new Error("Failed to convert SVG to PNG"));
    };
    image.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
  });
